import React, { useState, useEffect } from 'react';
import Markdown from 'markdown-to-jsx';
import { Wrench, CalendarClock } from 'lucide-react';
import { parseServerTime } from '../../../shared/utils/format';
import '../../../styles/features/incidents/MaintenanceNotice.css';

const DISMISS_KEY = 'neostatus.maintenance.dismissed';

// Dismissals persist across reloads. We key by window id + end_time so that an
// edited/rescheduled window resurfaces instead of staying hidden forever.
function loadDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

// A new update resurfaces a dismissed notice.
function dismissKey(w) {
  return `${w.id}:${w.end_time}:${w.updates?.[0]?.id || 0}`;
}

function useCountdown(targetDate) {
  const [timeLeft, setTimeLeft] = useState(() => Math.max(0, new Date(targetDate) - Date.now()));

  useEffect(() => {
    const tick = () => setTimeLeft(Math.max(0, new Date(targetDate) - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDate]);

  return timeLeft;
}

function formatCountdown(ms) {
  if (ms <= 0) return 'Starting now';
  const totalSecs = Math.floor(ms / 1000);
  const days  = Math.floor(totalSecs / 86400);
  const hours = Math.floor((totalSecs % 86400) / 3600);
  const mins  = Math.floor((totalSecs % 3600) / 60);
  const secs  = totalSecs % 60;

  if (days > 0)  return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m ${secs}s`;
}

// True when the window is currently running, accounting for explicit status.
function isActiveWindow(w, now) {
  if (w.status === 'in_progress') return true;
  return new Date(w.start_time) <= now && new Date(w.end_time) >= now;
}

function CountdownBadge({ startTime }) {
  const ms = useCountdown(startTime);
  return (
    <span className="maint-countdown">
      Starts in <strong>{formatCountdown(ms)}</strong>
    </span>
  );
}

function MaintenanceNotice({ windows = [] }) {
  const [dismissed, setDismissed] = useState(loadDismissed);
  const now = new Date();

  const persistDismiss = (w) => {
    setDismissed(prev => {
      const next = new Set(prev);
      next.add(dismissKey(w));
      try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };

  // Cancelled and finished windows never show; otherwise show active ones plus
  // anything starting within the next 24h.
  const relevant = windows.filter(w => {
    if (w.status === 'cancelled' || w.status === 'completed') return false;
    if (dismissed.has(dismissKey(w))) return false;
    if (isActiveWindow(w, now)) return true;
    const startsIn = new Date(w.start_time) - now;
    return startsIn > 0 && startsIn < 24 * 60 * 60 * 1000;
  });

  if (relevant.length === 0) return null;

  const formatRange = (start, end) => {
    const s = new Date(start);
    const e = new Date(end);
    const opts = { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return `${s.toLocaleString(undefined, opts)} – ${e.toLocaleString(undefined, opts)}`;
  };

  const scopeLabel = (w) => {
    const names = (w.endpoints || []).map(e => e.name).filter(Boolean);
    if (names.length === 0) return null;
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
  };

  return (
    <div className="maintenance-stack">
      {relevant.map(w => {
        const isActive = isActiveWindow(w, now);
        const scope = scopeLabel(w);
        return (
          <div key={w.id} className={`maintenance-notice ${isActive ? 'maint-active' : 'maint-upcoming'}`}>
            <div className="maint-icon">
              {isActive
                ? <Wrench size={18} color="#3b82f6" />
                : <CalendarClock size={18} color="#f59e0b" />}
            </div>
            <div className="maint-body">
              <strong className="maint-title">{w.title}</strong>
              {scope && (
                <span className="maint-scope">- {scope}</span>
              )}
              {isActive
                ? <span className="maint-label maint-label--active">In progress</span>
                : <CountdownBadge startTime={w.start_time} />
              }
              <p className="maint-time">{formatRange(w.start_time, w.end_time)}</p>
              {w.description && (
                <div className="maint-desc">
                  <Markdown options={{ disableParsingRawHTML: true }}>{w.description}</Markdown>
                </div>
              )}
              {w.updates?.length > 0 && (
                <ul className="maint-updates">
                  {w.updates.map((u) => (
                    <li key={u.id} className="maint-update">
                      <span className="maint-update_time">
                        {parseServerTime(u.created_at)?.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <div className="maint-update_message">
                        <Markdown options={{ disableParsingRawHTML: true }}>{u.message}</Markdown>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button
              className="maint-dismiss"
              onClick={() => persistDismiss(w)}
              title="Dismiss"
              aria-label="Dismiss maintenance notice"
            >
              ✕
            </button>
          </div>
        );
      })}
    </div>
  );
}

export default MaintenanceNotice;
